import { createSlice } from '@reduxjs/toolkit';
import { t } from '../../i18n/i18n';

/**
 * The signed-in trader's session. Filled by the `login` mutation's
 * `onQueryStarted` (see ../api/authApi.js) rather than by the calling component,
 * and persisted, so a reload keeps the user signed in.
 *
 * This file imports nothing from the API layer on purpose: `apiSlice` imports
 * `logout` from here for its 401 handling, and a matcher in the other direction
 * would close the cycle.
 */
const initialState = {
  user: null,          // { id, email, name, isStaff, isSuperUser }
  token: null,
  refreshToken: null,
  expiresAt: null,     // epoch ms, or null if the backend didn't say
  // "Remember for 30 days" on the sign-in form. It is what buys the session its
  // renewals — see `selectIsSessionValid` and ../api/apiSlice.js.
  remember: false,
  isAuthenticated: false,
  isNigerian: false,
  status: 'idle',      // 'idle' | 'loading' | 'failed'
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    /**
     * Takes the endpoint response as it comes off the wire. Sign-in returns the
     * Firebase credentials flat; signup nests the same keys under
     * `firebase_cred`. `expiresIn` is a string of seconds ("3600").
     *
     * `remember` is the one field that isn't the endpoint's: the sign-in form
     * adds it on the way past (see ../api/authApi.js), since whether to keep
     * the session alive is the trader's answer, not the backend's.
     */
    loginSuccess: (state, action) => {
      const body = action.payload ?? {};
      const cred = { ...body, ...body.firebase_cred };
      const expiresIn = Number(cred.expiresIn);

      state.user = {
        id: cred.localId ?? null,
        email: cred.email ?? null,
        // Firebase sends "" rather than omitting an unset display name.
        name: cred.displayName || null,
        isStaff: Boolean(body.user_stat?.isStaff),
        isSuperUser: Boolean(body.user_stat?.isSuperUser),
      };
      state.token = cred.idToken ?? null;
      state.refreshToken = cred.refreshToken ?? null;
      state.expiresAt = Number.isFinite(expiresIn) ? Date.now() + expiresIn * 1000 : null;
      state.remember = Boolean(body.remember);
      state.isAuthenticated = Boolean(state.token);
      state.status = 'idle';
      state.error = null;
    },
    loginFailure: (state, action) => {
      Object.assign(state, initialState, {
        status: 'failed',
        error: action.payload ?? t('Sign in failed'),
      });
    },
    /**
     * Sign-in answered `requires_2fa`: the password was right, but there is no
     * session yet. Distinct from `loginFailure` because nothing went wrong —
     * it only stands the form's spinner down while the challenge is answered,
     * and it must leave no error banner behind the dialog.
     */
    twoFactorRequired: (state) => {
      state.status = 'idle';
      state.error = null;
    },

    /**
     * A renewed access token from `/get/refreshtoken/` (see ../api/apiSlice.js).
     * The same session continuing, so the user and the staff flags are left
     * exactly as they are — only the credentials move.
     */
    tokenRefreshed: (state, action) => {
      const { token, refreshToken, expiresIn } = action.payload ?? {};
      if (!token) return;
      const seconds = Number(expiresIn);

      state.token = token;
      // Only when the endpoint rotated it; otherwise the stored one still works.
      if (refreshToken) state.refreshToken = refreshToken;
      state.expiresAt = Number.isFinite(seconds) ? Date.now() + seconds * 1000 : null;
      state.isAuthenticated = true;
    },

    /** Patch the cached user after a profile edit, without re-authenticating. */
    setUser: (state, action) => {
      state.user = state.user ? { ...state.user, ...action.payload } : action.payload;
    },
    setIsNigerian: (state, action) => {
      state.isNigerian = Boolean(action.payload);
    },
    logout: () => initialState,
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  twoFactorRequired,
  tokenRefreshed,
  setUser,
  setIsNigerian,
  logout,
} = authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
/** The long-lived Firebase token. Checkout posts it in the body, by design. */
export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsNigerian = (state) => state.auth.isNigerian;

/**
 * What the route guards ask. Stricter than `isAuthenticated`: the session is
 * persisted but the Firebase idToken only lives an hour, so a returning user can
 * hold a flag that its token no longer backs. Checking the expiry here sends them
 * to sign-in directly instead of flashing the dashboard until the first 401.
 *
 * An expired token is still a session for whoever asked to be remembered: the
 * first request 401s, `/get/refreshtoken/` answers it and the call is retried
 * (see ../api/apiSlice.js), so only a refresh that fails signs them out.
 * Without "Remember for 30 days" the session is meant to end with its token,
 * and an expired one sends them straight back to the form.
 */
export const selectIsSessionValid = (state) =>
  Boolean(state.auth.token) &&
  (state.auth.expiresAt == null ||
    state.auth.expiresAt > Date.now() ||
    Boolean(state.auth.remember && state.auth.refreshToken));

export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
